import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, Stack, TextField, Typography } from '@mui/material';
import OfferPortalApi, { OfferPreview, OfferRecord } from '../../api/OfferPortalApi';

/**
 * Review and send (Lloyd, Aug 18: "which once complete, then sends it to the client").
 *
 * Recording and sending are two steps rather than one. The email quotes fees and carries Lloyd's
 * own recommendation to accept — a message of that weight gets looked at before it leaves, which is
 * the rule the offer drafts and the bond-to-court delivery already follow.
 *
 * The preview is built server-side from the saved record rather than from the form, so what is
 * shown here cannot drift from what is sent.
 */
interface OfferSendPanelProps {
  offer: OfferRecord;
  onSent: (offer: OfferRecord) => void;
}

function OfferSendPanel({ offer, onSent }: OfferSendPanelProps) {
  const [preview, setPreview] = useState<OfferPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [confirmingResend, setConfirmingResend] = useState(false);

  const alreadySent = Boolean(offer.conveyedAt);

  const load = useCallback(async () => {
    setError(null);
    try {
      setPreview(await OfferPortalApi.previewOffer(offer.guid));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to build the preview');
    }
  }, [offer.guid]);

  useEffect(() => {
    void load();
  }, [load]);

  const send = async (resend: boolean) => {
    setSending(true);
    setError(null);
    setNotice(null);
    try {
      const result = await OfferPortalApi.sendOffer(offer.guid, resend);
      const went = [result.emailSent ? 'email' : null, result.smsSent ? 'text' : null]
        .filter(Boolean)
        .join(' and ');
      // Partial success is reported as such: one channel landing is a real outcome, and the staff
      // member needs to know the other did not rather than reading "sent" and assuming both.
      setNotice(
        result.problems.length
          ? `Sent by ${went}. ${result.problems.join(', and ')}.`
          : `Sent to the client by ${went}.`,
      );
      setConfirmingResend(false);
      onSent(result.offer);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send the offer');
    } finally {
      setSending(false);
    }
  };

  if (!preview) {
    return error ? (
      <Alert severity="error" onClose={() => setError(null)}>
        {error}
      </Alert>
    ) : (
      <Typography variant="body2" color="text.secondary">
        Building the preview…
      </Typography>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        Review and send to the client
      </Typography>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <Chip
          size="small"
          color={preview.recipientEmail ? 'default' : 'warning'}
          variant="outlined"
          label={preview.recipientEmail ? `Email: ${preview.recipientEmail}` : 'No email on file'}
        />
        <Chip
          size="small"
          color={preview.recipientPhone ? 'default' : 'warning'}
          variant="outlined"
          label={preview.recipientPhone ? `Text: ${preview.recipientPhone}` : 'No mobile number on file'}
        />
      </Stack>

      <TextField
        label="Email subject"
        value={preview.email.subject}
        fullWidth
        size="small"
        InputProps={{ readOnly: true }}
        sx={{ mb: 1 }}
      />
      <TextField
        label="Email body"
        value={preview.email.body}
        fullWidth
        multiline
        minRows={10}
        InputProps={{ readOnly: true }}
        sx={{ mb: 2 }}
      />
      <TextField
        label="Text message"
        value={preview.smsText}
        fullWidth
        size="small"
        multiline
        InputProps={{ readOnly: true }}
        helperText="The terms stay in the email — the text points the client to it."
        sx={{ mb: 2 }}
      />

      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {alreadySent && !confirmingResend && (
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="body2" color="text.secondary">
            This offer has already been sent to the client.
          </Typography>
          <Button size="small" onClick={() => setConfirmingResend(true)}>
            Send it again
          </Button>
        </Stack>
      )}

      {alreadySent && confirmingResend && (
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="body2">Send this offer to the client a second time?</Typography>
          <Button variant="contained" color="warning" disabled={sending} onClick={() => send(true)}>
            {sending ? 'Sending…' : 'Yes, send again'}
          </Button>
          <Button size="small" onClick={() => setConfirmingResend(false)}>
            Cancel
          </Button>
        </Stack>
      )}

      {!alreadySent && (
        <Button
          variant="contained"
          disabled={sending || (!preview.recipientEmail && !preview.recipientPhone)}
          onClick={() => send(false)}
        >
          {sending ? 'Sending…' : 'Send to client'}
        </Button>
      )}

      {!preview.recipientEmail && !preview.recipientPhone && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
          This client has neither an email address nor a mobile number on file, so there is nowhere
          to send the offer. Add one to their record first.
        </Typography>
      )}
    </Box>
  );
}

export default OfferSendPanel;
